"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, AlertTriangle } from "lucide-react";
import { exportToExcel } from "@/lib/excelExport";
import { formatCurrency } from "@/lib/utils";

interface OutstandingPaymentDashboardProps {
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    records: Array<{
      customerId: string;
      month: string;
      customerName: string;
      invoiceAmount: number;
      paymentAmount: number | null;
      adjustment: number;
      status: "paid" | "unpaid";
      remarks: string | null;
    }>;
    totals: {
      invoice: number;
      payment: number;
      adjustment: number;
    };
  };
}

export function OutstandingPaymentDashboard({
  data,
}: OutstandingPaymentDashboardProps) {
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const filteredRecords = data.records.filter((record) =>
    statusFilter === "all" ? true : record.status === statusFilter
  );

  const handleExport = () => {
    const exportData = filteredRecords.map((row) => ({
      "Customer ID": row.customerId,
      Month: row.month,
      "Customer Name": row.customerName,
      "Invoice Amount": row.invoiceAmount,
      "Payment Amount": row.paymentAmount ?? 0,
      "Add/Disc": row.adjustment,
      Status: row.status.toUpperCase(),
      Remarks: row.remarks || "-",
    }));

    exportToExcel(exportData, "Outstanding_Payments", "Payments");
  };

  const unpaidCount = data.records.filter((r) => r.status === "unpaid").length;
  const unpaidAmount = data.records
    .filter((r) => r.status === "unpaid")
    .reduce((sum, r) => sum + r.invoiceAmount, 0);

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Invoice</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(data.totals.invoice)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Paid</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(data.totals.payment)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Adjustments</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(data.totals.adjustment)}
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200 dark:border-red-900">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Outstanding
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(unpaidAmount)}
            </div>
            <p className="text-xs text-muted-foreground">
              {unpaidCount} unpaid invoices
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Data Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Payment Records</CardTitle>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export to Excel
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer ID</TableHead>
                <TableHead>Month</TableHead>
                <TableHead>Customer Name</TableHead>
                <TableHead className="text-right">Invoice</TableHead>
                <TableHead className="text-right">Payment</TableHead>
                <TableHead className="text-right">Add/Disc</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Remarks</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    No records found
                  </TableCell>
                </TableRow>
              ) : (
                filteredRecords.map((record, index) => (
                  <TableRow key={`${record.customerId}-${record.month}-${index}`}>
                    <TableCell className="font-medium">
                      {record.customerId}
                    </TableCell>
                    <TableCell>{record.month}</TableCell>
                    <TableCell>{record.customerName}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(record.invoiceAmount)}
                    </TableCell>
                    <TableCell className="text-right">
                      {record.paymentAmount !== null
                        ? formatCurrency(record.paymentAmount)
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(record.adjustment)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          record.status === "paid" ? "default" : "destructive"
                        }
                      >
                        {record.status.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {record.remarks || "-"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
